import { useEffect, useMemo, useState } from "react"

import { Collapse, Divider, Flex, Spin, Tag, Typography } from "antd"
import cls from "classname"
import { connect, useSelector } from "react-redux"
import { useLocation, useParams } from "react-router"
import { useNavigate } from "react-router-dom"

import ButtonComponent from "../../Components/Button"
import Icon from "../../Icon"
import CreditsAutomationPage from "../CreditsAutomationPage"
import CreditsPage from "../CreditsPage"
import { AddCommentRequestModal } from "./components/Modal/AddCommentRequest"
import { nextOpenKeys, toggleOpenKey } from "./openKeys"
// import RequestsDescriptors from './RequestsDescriptors'
import RequestsAutoRefund from "./RequestsAutoRefund"
import RequestsEnrollments from "./RequestsEnrollments"
import RequestsGifts from "./RequestsGifts"
import RequestsIntegrations from "./RequestsIntegrations"
import RequestsReceipts from "./RequestsReceipts"
import RequestsVerifiInform from "./RequestsVerifiInform"
import RequestsWithdrawal from "./RequestsWithdrawal"
import { useRequestsPageQuery } from "./useRequestsPageQuery"

import "../PersonalSettings/styles.less"
import "./styles.less"

const { Title, Text, Paragraph } = Typography

const tabsList = [
  {
    id: "receipts",
    text: "Receipt",
    icon: "receipt_bill",
    options: [
      { text: "Pending", id: "pending", key: "general" },
      { text: "Needs assistance", id: "needs_assistance" },
      { text: "In progress", id: "in_progress" },
      { text: "Active", id: "active" },
      { text: "Failed", id: "failed" },
      { text: "Unenrolled", id: "unenrolled" },
      { text: "Pending Unenroll", id: "pending_unenroll" },
      { text: "Unassigned", id: "unassigned" },
      { text: "Archive", id: "archive" },
      // Receipt is the only Requests page whose sub-tabs form two groups: descriptor enrollments
      // above, BA-level receipt-content updates below (spec §2.2 / §12.2). The marker is optional
      // and no other page sets it, so every other sidebar renders exactly as before.
      { divider: true },
      { text: "Pending update", id: "pending_update" },
      { text: "Update in progress", id: "update_in_progress" },
      { text: "Updated", id: "updated" },
      { text: "Updated failed", id: "updated_failed" }
    ]
  },
  {
    id: "enrollments",
    text: "Enrollments",
    icon: "piggy_bank",
    options: [
      { text: "Pending", id: "pending" },
      { text: "Needs assistance", id: "needs_assistance" },
      { text: "In progress", id: "in_progress" },
      { text: "Active", id: "active" },
      { text: "Failed", id: "failed" },
      { text: "Unenrolled", id: "unenrolled" },
      { text: "Pending Unenroll", id: "pending_unenroll" },
      { text: "Unassigned", id: "unassigned" },
      { text: "Archive", id: "archive" }
    ]
  },
  // {
  //   id: 'descriptors',
  //   text: 'Descriptors',
  //   icon: 'code_lines',
  //   options: [
  //     {text: 'New', id: 'new', key: 'general'},
  //     {text: 'Processed', id: 'processed'},
  //     {text: 'Archive', id: 'archive'},
  //     {text: 'Further Communication', id: 'need_communication'},
  //   ]
  // },
  {
    id: "auto_refund",
    text: "Auto-refund",
    icon: "money_hand",
    options: [
      { text: "New", id: "new", key: "general" },
      { text: "Processed", id: "processed" },
      { text: "Archive", id: "archive" }
    ]
  },
  {
    id: "integrations",
    text: "Integrations",
    icon: "list_sparkle"
  },
  {
    id: "credits",
    text: "Credits",
    icon: "piggy_bank",
    options: [
      { text: "Pending", id: "pending", key: "general" },
      { text: "Processed", id: "processed" },
      { text: "Completed", id: "completed" },
      { text: "Awaiting proof", id: "awaiting_proof" },
      { text: "Archive", id: "archive" }
    ]
  },
  {
    id: "credits_automation",
    text: "Credits automation",
    icon: "automation_loop",
    options: [
      { text: "Pending", id: "pending", key: "general" },
      { text: "Action needed", id: "action_needed" },
      { text: "Active", id: "active" },
      { text: "Lost", id: "lost" },
      { text: "Disabled", id: "disabled" }
    ]
  },
  {
    id: "gift",
    text: "Gifts",
    icon: "gift"
  },
  {
    id: "withdrawal",
    text: "Withdrawals",
    icon: "money_hand"
  },
  {
    id: "verifi_inform",
    text: "Verifi Inform",
    icon: "bank_card"
  }
]

const RequestsPage = ({ businessAccountFetching, statistics }) => {
  const { hash } = useParams() // enrollments, auto_refund, integrations, credits
  const { search } = useLocation()
  const { folder, status, addCommentRequestID } = useRequestsPageQuery(search)
  const navigate = useNavigate()
  const isRestrictedVerifiInformAdmin = useSelector((state) => state.auth.userInfo?.role?.id === 5)

  // For `receipts` tab, folder-selector value is stored under `?status=`, not `?folder=`.
  const activeFolder = hash === "receipts" ? status : folder

  function handleNavigate(subPage, folderValue = null) {
    const paramName = subPage === "receipts" ? "status" : "folder"
    navigate(`/requests/${subPage}${folderValue ? `?${paramName}=${folderValue}` : ""}`)
  }

  useEffect(() => {
    //document.title = 'Requests - Chargeback'
    /*if (hash) {
      if (!/enrollments|descriptors|auto_refund|integrations/i.test(hash)) {
        navigate(`/requests/enrollments?folder=new`)
      }
    }*/
  }, [hash])

  // CHA1-3190: RequestsPage owns the open groups; the header click below sets them, so the route
  // effect only seeds them and covers a hash change that did not come from a click (direct URL,
  // Back/Forward, redirects). It never reopens a group the user collapsed: the hash is unchanged.
  const [openKeys, setOpenKeys] = useState(hash ? [hash] : [])

  useEffect(() => {
    setOpenKeys((current) => nextOpenKeys(current, hash))
  }, [hash])

  useEffect(() => {
    if (isRestrictedVerifiInformAdmin && hash === "verifi_inform") {
      navigate("/requests/enrollments?folder=new", { replace: true })
    }
  }, [hash, isRestrictedVerifiInformAdmin, navigate])

  const content = {
    receipts: <RequestsReceipts />,
    enrollments: <RequestsEnrollments />,
    // descriptors: <RequestsDescriptors/>,
    auto_refund: <RequestsAutoRefund />,
    integrations: <RequestsIntegrations />,
    credits: <CreditsPage />,
    credits_automation: <CreditsAutomationPage />,
    gift: <RequestsGifts />,
    withdrawal: <RequestsWithdrawal />,
    verifi_inform: <RequestsVerifiInform />
  }

  const visibleTabsList = useMemo(
    () =>
      isRestrictedVerifiInformAdmin
        ? tabsList.filter((tab) => tab.id !== "verifi_inform")
        : tabsList,
    [isRestrictedVerifiInformAdmin]
  )

  const items = useMemo(
    () =>
      visibleTabsList.map((tab) => ({
        key: tab.id,
        showArrow: false,
        label: (
          <ButtonComponent
            type="text"
            size="large"
            //onClick={() => handleNavigate(tab.id, tab?.options?.[0]?.id || null)}
            onClick={() => {
              // Same tab: only toggle the group. Navigating to the route we are already on would
              // drop ?folder/?status and refetch the list.
              if (hash === tab.id && !(status || folder)) {
                setOpenKeys((current) => toggleOpenKey(current, tab.id))
                return
              }

              // Other tab: open its group in the same batch as the navigation, one render.
              setOpenKeys([tab.id])
              handleNavigate(tab.id)
            }}
            data-testid={`deflect-admin-sidebar-tab-${tab.id}`}
            className={cls("flex", {
              with_arrow: tab?.options?.length,
              // The arrow shows the open state, not the selected tab (they differ once the
              // current group is collapsed).
              with_arrow_active: openKeys.includes(tab.id),
              active: hash === tab.id && !activeFolder
            })}
            icon={tab.icon ? <Icon type={tab.icon} /> : <span />}
            block
          >
            <Text
              type={hash === tab.id && !activeFolder ? "" : "secondary"}
              ellipsis={{ tooltip: tab.text }}
              style={{ marginRight: "auto" }}
            >
              {tab.text}
            </Text>
            {tab?.options?.length ? (
              <Flex>
                <Tag
                  color="processing"
                  bordered={false}
                  style={{
                    margin: "0 8px",
                    padding: 0,
                    background: "none",
                    border: "none"
                  }}
                >
                  {statistics?.[`${tab.id}_counter`]?.["all"] || 0}
                </Tag>
                <Icon
                  onClick={(e) => {
                    e?.stopPropagation()
                    setOpenKeys((current) => toggleOpenKey(current, tab.id))
                  }}
                  type="chevron_down_small"
                  className="arrow-icon"
                />
              </Flex>
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {statistics?.[`${tab.id}_counter`] || 0}
              </Text>
            )}
          </ButtonComponent>
        ),
        children: (
          <Flex vertical={true} gap={4}>
            {tab?.options?.map((el, index) => {
              if (el.divider) {
                return (
                  <Divider
                    key={`${tab.id}-divider-${index}`}
                    style={{ margin: "4px 0" }}
                    data-testid="deflect-admin-sidebar-subtab-divider"
                  />
                )
              }

              let isActive = hash === tab.id && activeFolder === el.id
              let count = statistics?.[`${tab.id}_counter`]?.[el.id] || "-"
              return (
                <ButtonComponent
                  key={el.id}
                  type="text"
                  size="large"
                  onClick={() => handleNavigate(tab.id, el?.id)}
                  data-testid={`deflect-admin-sidebar-subtab-${tab.id}-${el.id}`}
                  className={cls("flex", { active: isActive })}
                  block
                >
                  <Paragraph
                    type={isActive ? "" : "secondary"}
                    ellipsis={{ tooltip: el.text, symbol: "...", rows: 1 }}
                    style={{ marginRight: "auto", marginBottom: 0 }}
                  >
                    {el.text}
                  </Paragraph>
                  {el?.id === "new" ? (
                    <Tag
                      color="processing"
                      bordered={false}
                      style={{
                        margin: "0 0 0 8px",
                        padding: 0,
                        background: "none",
                        border: "none"
                      }}
                    >
                      {count}
                    </Tag>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {count}
                    </Text>
                  )}
                </ButtonComponent>
              )
            })}
          </Flex>
        ),
        styles: {
          header: { padding: "4px 0" },
          body: { padding: "0 0 0 28px" }
        }
      })),
    [hash, activeFolder, statistics, visibleTabsList, openKeys]
  )

  return (
    <div className={"settings-page"}>
      <div className="sider" data-testid="deflect-admin-sidebar">
        <Title level={3} style={{ textAlign: "left", marginBottom: 24 }}>
          Requests
        </Title>

        {/* collapsible="icon" with showArrow=false: rc-collapse no longer toggles on the header
            click, the ButtonComponent above is the only handler. */}
        <Collapse ghost={true} collapsible="icon" items={items} activeKey={openKeys} />
      </div>
      <div className={cls("content", "requests")}>
        <Spin spinning={businessAccountFetching}>{content[hash]}</Spin>
      </div>
      <AddCommentRequestModal requestType={hash} />
    </div>
  )
}

const mapStateToProps = (state) => ({
  statistics: state.businessAccounts.statistics,
  businessAccountFetching: state.user.businessAccountFetching,
  theme: state.nav.theme
})

export default connect(mapStateToProps, null)(RequestsPage)
